 'use client';

import Script from 'next/script';
import { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';

export default function AutoAdsScripts() {
  const pathname = usePathname();
  const [scriptsLoaded, setScriptsLoaded] = useState({ newsletter: false, ads: false });

  if (process.env.NODE_ENV === 'development') {
    return null;
  }
  const rescanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const adsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const nlPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const placementPollsRef = useRef<Map<Element, ReturnType<typeof setInterval>>>(new Map());
  const placementTimeoutsRef = useRef<Map<Element, ReturnType<typeof setTimeout>>>(new Map());
  const targetPlacementIds = ['cmm0jm5xc000ljg95dfktcxlh', 'cmjiqm6p1000512xot0l3pocy'];
  const forcedInitRef = useRef<boolean>(false);

  const anyReady = scriptsLoaded.newsletter || scriptsLoaded.ads;

  // Utility function to check if element is visible and has dimensions
  const isElementReady = (element: Element): boolean => {
    const rect = element.getBoundingClientRect();
    const el = element as HTMLElement;
    const styles = window.getComputedStyle(el);
    const visible =
      styles.visibility !== 'hidden' &&
      styles.display !== 'none' &&
      parseFloat(styles.opacity || '1') > 0;
    return rect.width > 0 && rect.height > 0 && visible;
  };

  // Combined logic for rescanning
  useEffect(() => {
    if (!anyReady) return;

    // Wait for DOM to be stable before starting
    const waitForDOMStability = (callback: () => void, maxWait = 3000) => {
      let timeoutId: ReturnType<typeof setTimeout>;
      let rafId: ReturnType<typeof requestAnimationFrame>;
      let checks = 0;
      const maxChecks = 5;
      
      const checkStability = () => {
        const adElements = document.querySelectorAll('[data-ad-placement]');
        const allReady = Array.from(adElements).every(el => isElementReady(el));
        
        if (allReady && checks >= maxChecks) {
          clearTimeout(timeoutId);
          callback();
        } else {
          checks++;
          rafId = requestAnimationFrame(() => setTimeout(checkStability, 100));
        }
      };
      
      timeoutId = setTimeout(() => {
        cancelAnimationFrame(rafId);
        callback(); // Proceed even if not perfectly stable
      }, maxWait);
      
      checkStability();
    };

    const rescan = () => {
      if (rescanTimeoutRef.current) {
        clearTimeout(rescanTimeoutRef.current);
      }
      rescanTimeoutRef.current = setTimeout(() => {
        try {
          (window as any).MarketingNewsletter?.rescan?.();
          (window as any).AutoAds?.init?.();
        } catch (error) {
          console.error('Rescan failed:', error);
        }
      }, 300); // Debounce rescans
    };

    const forceInitExistingPlacements = () => {
      if (forcedInitRef.current) return;
      const els = Array.from(document.querySelectorAll('[data-ad-placement]'));
      els.forEach(el => {
        if ((el as Element).getAttribute('data-ad-processed') === 'true') return;
        const parent = el.parentNode;
        if (!parent) return;
        const clone = el.cloneNode(true) as Element;
        parent.replaceChild(clone, el);
      });
      forcedInitRef.current = true;
      rescan();
    };

    const onLoadRescan = () => {
      waitForDOMStability(() => {
        forceInitExistingPlacements();
        rescan();
      });
    };
    
    if (document.readyState === 'complete') {
      waitForDOMStability(() => {
        forceInitExistingPlacements();
        rescan();
      });
    } else {
      window.addEventListener('load', onLoadRescan, { once: true });
    }

    // Initial rescan with delay to simulate DevTools timing
    const initialTimeout = setTimeout(rescan, 100); // Increased from 0 to 100ms
    retryTimeoutsRef.current.push(
      setTimeout(rescan, 800),   // Increased from 500 to 800ms
      setTimeout(rescan, 2000),  // Increased from 1500 to 2000ms
      setTimeout(rescan, 4000),  // Increased from 3000 to 4000ms
      setTimeout(rescan, 7000)   // Increased from 5000 to 7000ms
    );

    // IntersectionObserver with visibility check
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && isElementReady(entry.target)) {
          // Add small delay when element becomes visible
          setTimeout(rescan, 200);
        }
      });
    }, { threshold: 0.1 });

    const observeContainers = () => {
      document.querySelectorAll('[data-ad-placement],[data-newsletter-id]').forEach(el => {
        io.observe(el);
      });
    };

    const startPlacementPolls = () => {
      const els = Array.from(document.querySelectorAll('[data-ad-placement]'));
      els.forEach(el => {
        if ((el as Element).getAttribute('data-ad-processed') === 'true') return;
        if (placementPollsRef.current.has(el)) return;
        
        // Wait for element to be ready before starting polling
        const checkReadyAndPoll = () => {
          if (!isElementReady(el)) {
            setTimeout(checkReadyAndPoll, 100);
            return;
          }
          
          const poll = setInterval(() => {
            if ((el as Element).getAttribute('data-ad-processed') === 'true') {
              const timer = placementPollsRef.current.get(el);
              if (timer) clearInterval(timer);
              placementPollsRef.current.delete(el);
              const to = placementTimeoutsRef.current.get(el);
              if (to) clearTimeout(to);
              placementTimeoutsRef.current.delete(el);
            } else {
              try {
                (window as any).AutoAds?.init?.();
              } catch (error) {
                console.error('Placement rescan failed:', error);
              }
            }
          }, 400);
          placementPollsRef.current.set(el, poll);
          const stop = setTimeout(() => {
            const timer = placementPollsRef.current.get(el);
            if (timer) clearInterval(timer);
            placementPollsRef.current.delete(el);
            const id = (el as Element).getAttribute('data-ad-placement') || '';
            const processed = (el as Element).getAttribute('data-ad-processed') === 'true';
            if (!processed && targetPlacementIds.includes(id)) {
              console.error(`Ad placement failed to process on first load: ${id} at ${pathname}`);
            }
          }, 15000);
          placementTimeoutsRef.current.set(el, stop);
        };
        
        checkReadyAndPoll();
      });
    };

    // MutationObserver
    const mo = new MutationObserver((mutations) => {
      const hasAdContainers = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(node =>
          node.nodeType === 1 &&
          ((node as Element).matches?.('[data-ad-placement],[data-newsletter-id]') ||
            (node as Element).querySelector?.('[data-ad-placement],[data-newsletter-id]'))
        )
      );

      if (hasAdContainers) {
        observeContainers();
        rescan();
        startPlacementPolls();
      }
    });

    observeContainers();
    mo.observe(document.body, { childList: true, subtree: true });
    forceInitExistingPlacements();
    startPlacementPolls();

    // Cleanup
    return () => {
      clearTimeout(initialTimeout);
      if (rescanTimeoutRef.current) {
        clearTimeout(rescanTimeoutRef.current);
      }
      retryTimeoutsRef.current.forEach(t => clearTimeout(t));
      retryTimeoutsRef.current = [];
      placementPollsRef.current.forEach(i => clearInterval(i));
      placementPollsRef.current.clear();
      placementTimeoutsRef.current.forEach(t => clearTimeout(t));
      placementTimeoutsRef.current.clear();
      window.removeEventListener('load', onLoadRescan);
      io.disconnect();
      mo.disconnect();
    };
  }, [pathname, anyReady]);

  const handleScriptLoad = (scriptType: 'newsletter' | 'ads') => {
    const checkReady = () => {
      const ready =
        scriptType === 'newsletter'
          ? typeof (window as any).MarketingNewsletter?.rescan === 'function'
          : typeof (window as any).AutoAds?.init === 'function';
      if (ready) {
        if (scriptType === 'newsletter' && nlPollRef.current) {
          clearInterval(nlPollRef.current);
          nlPollRef.current = null;
        }
        if (scriptType === 'ads' && adsPollRef.current) {
          clearInterval(adsPollRef.current);
          adsPollRef.current = null;
        }
        
        // Add DevTools-like delay for ads specifically
        if (scriptType === 'ads') {
          setTimeout(() => {
            setScriptsLoaded(prev => ({ ...prev, [scriptType]: true }));
          }, 500); // 500ms delay to simulate DevTools timing
        } else {
          setScriptsLoaded(prev => ({ ...prev, [scriptType]: true }));
        }
        return true;
      }
      return false;
    };
    if (checkReady()) return;
    const interval = setInterval(checkReady, 100);
    if (scriptType === 'newsletter') nlPollRef.current = interval;
    if (scriptType === 'ads') adsPollRef.current = interval;
  };

  const handleScriptError = (scriptType: 'newsletter' | 'ads') => {
    console.error(`Failed to load ${scriptType} script`);
  };

  return (
    <>
      <Script
        src="https://ads.autoads.co.za/newsletter.js?v=2"
        strategy="lazyOnload"
        onLoad={() => handleScriptLoad('newsletter')}
        onError={() => handleScriptError('newsletter')}
      />
      <Script
        src="https://ads.autoads.co.za/ad-script.js?v=2"
        strategy="lazyOnload"
        onLoad={() => handleScriptLoad('ads')}
        onError={() => handleScriptError('ads')}
      />
    </>
  );
}
